import { useCallback, useMemo, useState } from "react"
import { matchPath, useLocation } from "react-router-dom"
import get from "lodash/get"
import orderBy from "lodash/orderBy"

import { prepareAppNavigationConfig } from "./utils"
import config from "config"
import { useVenue } from "providers/venue"
import {
  IRouteConfig,
  IRouteConfigOptions,
  IRouteGroups,
} from "utils/extension"

type RouteOption = Pick<IRouteConfig, "path" | "options">

const DEFAULT_ROUTE_OPTIONS: IRouteConfigOptions = { showAppLayoutNav: true }

export const useAppNavigation = () => {
  const deviceType = config("device", "mobile")
  const location = useLocation()
  const { appConfig } = useVenue()
  const [routeOptions, setRouteOptions] = useState<RouteOption[]>()
  /**
   * Main menus: used with bottom navigation on mobile.
   * Side menus: used with sidebar menus (mobile) and menu bars (kiosk).
   */
  const { mainMenus, sideMenus } = useMemo(
    () => prepareAppNavigationConfig(appConfig, deviceType),
    [appConfig, deviceType]
  )

  /** Find matched path to add "select" state to navs
   *  - add /* so that all sub-pages will match too, (eg. /maps/ordinal/1, /maps/place/:placeId will also set `/maps` to selected)
   */
  const orderedMenus = useMemo(
    () => orderBy(sideMenus, ["to"], ["desc"]),
    [sideMenus]
  )

  const matchedPath = useMemo(() => {
    const { pathname: currentPathName, search = "" } = location
    const currentQueryParams = new URLSearchParams(search)
    return orderedMenus.find((nav) => {
      const [navPathname, navQueryString = ""] = get(nav, "to", "").split("?")
      const navQueryParams = new URLSearchParams(navQueryString)
      const isMatchedPath = currentPathName.indexOf(navPathname) === 0

      if (!isMatchedPath) return false

      //@ts-ignore
      if (navQueryParams.size === 0) return isMatchedPath

      // If the config path includes a query string, we will prioritize matching the query string.
      for (const p of navQueryParams) {
        const [key, value] = p
        //@ts-ignore
        if (!currentQueryParams.has(key, value)) return false
      }

      return true
    })
  }, [orderedMenus, location])

  const matchedRouteOption = useMemo(
    () =>
      routeOptions?.find(({ path }) => matchPath(path, location.pathname))
        ?.options || {},
    [routeOptions, location.pathname]
  )
  const updateRouteOptions = useCallback((rotueGroups: IRouteGroups) => {
    const {
      baseRoutes = [],
      mapRoutes = [],
      pageRoutes = [],
    } = rotueGroups || {}

    const normalizedBaseRouteOptions = baseRoutes.map(
      ({ path = "", options }) => ({
        path: `/${path}`,
        options: { ...DEFAULT_ROUTE_OPTIONS, ...options },
      })
    )
    const normalizedMapRouteOptions = mapRoutes.map(({ path, options }) => ({
      path: `/maps/${path}`,
      options: { ...DEFAULT_ROUTE_OPTIONS, ...options },
    }))
    const normalizedPageRouteOptions = pageRoutes.map(({ path, options }) => ({
      path: `/${path}`,
      options: { ...DEFAULT_ROUTE_OPTIONS, ...options },
    }))
    const routeOptions = [
      ...normalizedBaseRouteOptions,
      ...normalizedPageRouteOptions,
      ...normalizedMapRouteOptions,
    ]
    setRouteOptions(routeOptions)
  }, [])

  return {
    navigations: sideMenus,
    matchedPath,
    mainMenus,
    sideMenus,
    routeOptions,
    matchedRouteOption,
    updateRouteOptions,
  }
}
